import React, { useRef } from 'react';
import { Button } from './Button';

export interface FileUploadProps {
  label?: string;
  error?: string;
  helperText?: string;
  accept?: string;
  multiple?: boolean;
  maxFiles?: number;
  maxSizeMB?: number;
  value?: File[];
  onChange?: (files: File[]) => void;
  disabled?: boolean;
}

const FileUpload = React.forwardRef<HTMLInputElement, FileUploadProps>(
  ({ 
    label, 
    error, 
    helperText, 
    accept, 
    multiple = false, 
    maxFiles = 5,
    maxSizeMB = 5,
    value = [],
    onChange,
    disabled,
  }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const fileId = `file-${Math.random().toString(36).substr(2, 9)}`;

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files || !onChange) return;

      const files = Array.from(e.target.files);
      
      // Validate file size
      const validFiles = files.filter((file) => {
        const sizeInMB = file.size / (1024 * 1024);
        if (sizeInMB > maxSizeMB) {
          alert(`File ${file.name} exceeds maximum size of ${maxSizeMB}MB`);
          return false;
        }
        return true;
      });

      // Limit number of files
      const limitedFiles = multiple ? validFiles.slice(0, maxFiles) : validFiles.slice(0, 1);
      
      if (multiple) {
        onChange([...value, ...limitedFiles].slice(0, maxFiles));
      } else {
        onChange(limitedFiles);
      }

      // Reset input
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    };

    const removeFile = (index: number) => {
      if (onChange && value) {
        const newFiles = value.filter((_, i) => i !== index);
        onChange(newFiles);
      }
    };

    const formatFileSize = (bytes: number): string => {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    };

    return (
      <div className="space-y-2">
        {label && (
          <label className="text-sm font-medium leading-none text-secondary-700">
            {label}
          </label>
        )}
        <div className="space-y-2">
          <input
            ref={inputRef}
            type="file"
            id={fileId}
            accept={accept}
            multiple={multiple}
            onChange={handleFileChange}
            disabled={disabled}
            className="hidden"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
          >
            Choose Files
          </Button>
          {value && value.length > 0 && (
            <div className="mt-2 space-y-1">
              {value.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 bg-secondary-50 rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-secondary-900 truncate">
                      {file.name}
                    </p>
                    <p className="text-xs text-secondary-500">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="ml-2 text-red-600 hover:text-red-800"
                  >
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        {error && (
          <p className="text-sm text-red-600 flex items-center gap-1">
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {error}
          </p>
        )}
        {helperText && !error && (
          <p className="text-sm text-secondary-600">{helperText}</p>
        )}
      </div>
    );
  }
);

FileUpload.displayName = 'FileUpload';

export { FileUpload };

